'use client';

import { useState } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { InternalNetworkDto } from '@terra/shared-contracts-internal-network';
import {
  DEFAULT_SCANNING_SCHEDULE,
  ScanningConfigurationCard,
  type ScanningConfigurationChange,
} from '../../settings/automation/ScanningConfigurationCard';
import { SettingsSection } from '../../settings/SettingsSection';
import {
  resolveDiscoveryScheduleChange,
  resolveScenarioExecutionChange,
  type InternalNetworkExecutionPersistRequest,
} from './internal-execution.model';

type InternalNetworkExecutionAutomationSectionProps = {
  network: Pick<
    InternalNetworkDto,
    'schedule' | 'executionSchedule' | 'smokeTestEnabled'
  >;
  disabled: boolean;
  isUpdating: boolean;
  onPersist: (
    request: InternalNetworkExecutionPersistRequest,
  ) => Promise<boolean>;
};

const hasSchedule = (schedule: InternalNetworkDto['schedule'] | undefined) =>
  schedule !== null && schedule !== undefined;

export function InternalNetworkExecutionAutomationSection({
  network,
  disabled,
  isUpdating,
  onPersist,
}: InternalNetworkExecutionAutomationSectionProps) {
  const t = useTranslations('designSystem.networkSettings.internalExecution');
  const [syncedSchedules, setSyncedSchedules] = useState({
    schedule: network.schedule,
    executionSchedule: network.executionSchedule,
  });
  const [discoveryScheduleEnabled, setDiscoveryScheduleEnabled] = useState(() =>
    hasSchedule(network.schedule),
  );
  const [executionScheduleEnabled, setExecutionScheduleEnabled] = useState(() =>
    hasSchedule(network.executionSchedule),
  );

  if (
    syncedSchedules.schedule !== network.schedule ||
    syncedSchedules.executionSchedule !== network.executionSchedule
  ) {
    setSyncedSchedules({
      schedule: network.schedule,
      executionSchedule: network.executionSchedule,
    });
    setDiscoveryScheduleEnabled(hasSchedule(network.schedule));
    setExecutionScheduleEnabled(hasSchedule(network.executionSchedule));
  }

  const persistRequest = (
    request: InternalNetworkExecutionPersistRequest | null,
  ) => {
    if (!request) {
      return;
    }

    void onPersist(request);
  };

  const handleDiscoveryScheduleChange = (
    change: ScanningConfigurationChange,
  ) => {
    if (disabled) {
      return;
    }

    if (typeof change.limitScheduleEnabled === 'boolean') {
      setDiscoveryScheduleEnabled(change.limitScheduleEnabled);
    }

    persistRequest(resolveDiscoveryScheduleChange(change));
  };

  const handleScenarioExecutionChange = (
    change: ScanningConfigurationChange,
  ) => {
    if (disabled) {
      return;
    }

    if (
      typeof change.smokeTestEnabled !== 'boolean' &&
      typeof change.limitScheduleEnabled === 'boolean'
    ) {
      setExecutionScheduleEnabled(change.limitScheduleEnabled);
    }

    persistRequest(
      resolveScenarioExecutionChange({ change, executionScheduleEnabled }),
    );
  };

  return (
    <VStack alignItems="stretch" gap={6}>
      <SettingsSection
        title={t('discoveryScheduleSection')}
        description={t('discoveryScheduleSectionDescription')}
      >
        <ScanningConfigurationCard
          scheduleOnlyMode
          limitScheduleLabel={t('limitDiscoverySchedule.label')}
          limitScheduleTooltip={t('limitDiscoverySchedule.tooltip')}
          scheduleDetailsTitle={t('scheduleDetailsTitle')}
          smokeTestEnabled={false}
          limitScheduleEnabled={discoveryScheduleEnabled}
          automationSchedule={network.schedule ?? DEFAULT_SCANNING_SCHEDULE}
          disabled={disabled}
          isUpdating={isUpdating}
          onChange={handleDiscoveryScheduleChange}
        />
      </SettingsSection>

      <SettingsSection
        title={t('scenarioExecutionSection')}
        description={t('scenarioExecutionSectionDescription')}
      >
        <ScanningConfigurationCard
          smokeTestEnabled={network.smokeTestEnabled ?? false}
          limitScheduleEnabled={executionScheduleEnabled}
          automationSchedule={
            network.executionSchedule ?? DEFAULT_SCANNING_SCHEDULE
          }
          runAutomatedLabel={t('runAutomatedScenarios.label')}
          runAutomatedTooltip={t('runAutomatedScenarios.tooltip')}
          limitScheduleLabel={t('limitExecutionSchedule.label')}
          limitScheduleTooltip={t('limitExecutionSchedule.tooltip')}
          disabled={disabled}
          isUpdating={isUpdating}
          onChange={handleScenarioExecutionChange}
        />
      </SettingsSection>
    </VStack>
  );
}
